import type { SimpleFieldConfig, EntityWithRelations, BaseEntity } from "../types";

// ============================================================================
// FILTERING LOGIC
// ============================================================================

/**
 * Applies advanced filter logic to a single item
 */
export const applyAdvancedFilter = (
  item: EntityWithRelations,
  fieldId: string,
  filter: { type: string; value: any },
  fields: Record<string, SimpleFieldConfig>
): boolean => {
  const fieldConfig = fields[fieldId];
  const itemValue = item[fieldId as keyof typeof item];

  if (!fieldConfig || itemValue === null || itemValue === undefined) return false;

  // Special handling for tags field - parse comma-separated values
  if (fieldId === "tags" && filter.type === "contains" && Array.isArray(itemValue)) {
    const searchTags = String(filter.value)
      .split(",")
      .map((tag: string) => tag.trim().toLowerCase())
      .filter((tag: string) => tag.length > 0);

    if (searchTags.length === 0) return false;

    // Check if any of the search tags match any of the item's tags.
    // After Array.isArray() guard, itemValue is narrowed to an array;
    // element type is unknown so we stringify before comparing.
    const tagValues: readonly unknown[] = itemValue;
    return searchTags.some((searchTag: string) =>
      tagValues.some((itemTag) =>
        String(itemTag).toLowerCase().includes(searchTag)
      )
    );
  }

  switch (filter.type) {
    case "exact":
      // Handle both single values and arrays for "Any of (OR)" functionality
      if (Array.isArray(filter.value)) {
        // For arrays, check if the item value (as string) is included in the filter array
        return filter.value.some(
          (val: any) => String(val).toLowerCase() === String(itemValue).toLowerCase()
        );
      }
      // For single values, do exact match (case-insensitive)
      return String(itemValue).toLowerCase() === String(filter.value).toLowerCase();
    case "contains":
      return String(itemValue).toLowerCase().includes(String(filter.value).toLowerCase());
    case "startsWith":
      return String(itemValue).toLowerCase().startsWith(String(filter.value).toLowerCase());
    case "endsWith":
      return String(itemValue).toLowerCase().endsWith(String(filter.value).toLowerCase());
    case "notNull":
      // Check if the field has any value (not null, undefined, or empty string)
      if (itemValue === null || itemValue === undefined) return false;
      if (typeof itemValue === "string") return itemValue.trim().length > 0;
      if (Array.isArray(itemValue)) return itemValue.length > 0;
      return true; // For other types (numbers, booleans, objects), consider them as having value
    case "range":
    case "dateRange":
      if (filter.value.min !== undefined && filter.value.max !== undefined) {
        if (typeof itemValue !== "string" && typeof itemValue !== "number" && !(itemValue instanceof Date)) {
          return false;
        }
        const value = new Date(itemValue);
        const min = new Date(filter.value.min);
        const max = new Date(filter.value.max);
        return value >= min && value <= max;
      }
      return true;
    case "timeRange":
      if (filter.value.min !== undefined && filter.value.max !== undefined) {
        // For time comparison, we need to compare time strings (HH:MM format)
        const itemTime = String(itemValue).substring(0, 5); // Get HH:MM part
        const minTime = String(filter.value.min).substring(0, 5);
        const maxTime = String(filter.value.max).substring(0, 5);
        return itemTime >= minTime && itemTime <= maxTime;
      }
      return true;
    case "numberRange":
      if (filter.value.min !== undefined && filter.value.max !== undefined) {
        const value = Number(itemValue);
        return value >= filter.value.min && value <= filter.value.max;
      }
      return true;
    case "multiSelect":
      // For multiSelect, filter.value should be an array of selected values
      if (!Array.isArray(filter.value) || filter.value.length === 0) return false;
      // Check if the item value (as string) is included in the filter array
      return filter.value.some(
        (val: any) => String(val).toLowerCase() === String(itemValue).toLowerCase()
      );
    case "company-select":
    case "client-select":
    case "user-select":
      // For select filters, do exact match (case-insensitive). `user-select`
      // holds an ARRAY of app_users.id (MultiUserSelect) — match any of them.
      if (!filter.value) return false;
      if (Array.isArray(filter.value)) {
        if (filter.value.length === 0) return false;
        return filter.value.some(
          (val) => String(val).toLowerCase() === String(itemValue).toLowerCase()
        );
      }
      return String(itemValue).toLowerCase() === String(filter.value).toLowerCase();
    default:
      return true;
  }
};

// ============================================================================
// SORTING LOGIC
// ============================================================================

/** Comparable scalar — values supported by JS `<`/`>` operators. */
type SortableScalar = string | number | Date | boolean;

/**
 * Coerce an arbitrary indexed field value into a comparable scalar.
 * Returns `null` for values that cannot be ordered (objects, arrays, etc.) so
 * the caller can treat them as missing.
 */
function toSortable(value: unknown): SortableScalar | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }
  if (value instanceof Date) return value;
  return null;
}

function compareSortable(a: SortableScalar, b: SortableScalar): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/**
 * Applies multi-column sorting to two items
 */
export const applyMultiColumnSort = (
  a: EntityWithRelations,
  b: EntityWithRelations,
  sortColumns: Array<{ field: string; direction: "asc" | "desc"; priority: number }>
): number => {
  if (sortColumns.length === 0) return 0;

  // Sort by priority order
  const sortedColumns = [...sortColumns].sort((x, y) => x.priority - y.priority);

  for (const sortCol of sortedColumns) {
    const aSortable = toSortable(a[sortCol.field as keyof typeof a]);
    const bSortable = toSortable(b[sortCol.field as keyof typeof b]);

    if (aSortable === bSortable) continue;
    if (aSortable === null) return 1;
    if (bSortable === null) return -1;

    const comparison = compareSortable(aSortable, bSortable);
    const result = sortCol.direction === "asc" ? comparison : -comparison;

    if (result !== 0) return result;
  }

  return 0;
};

/**
 * Applies single column sorting (backward compatibility)
 */
export const applySingleColumnSort = (
  a: EntityWithRelations,
  b: EntityWithRelations,
  sortField: string,
  sortDirection: "asc" | "desc"
): number => {
  const aSortable = toSortable(a[sortField as keyof typeof a]);
  const bSortable = toSortable(b[sortField as keyof typeof b]);

  if (aSortable === bSortable) return 0;
  if (aSortable === null) return 1;
  if (bSortable === null) return -1;

  const comparison = compareSortable(aSortable, bSortable);
  return sortDirection === "asc" ? comparison : -comparison;
};
